/**
 * Bridge nativo per l'app OSS (Android WebView / iOS WKWebView), anche su tablet.
 * Stesso schema di Drive e Family:
 *  - user agent "AffidyaOssAndroid/<ver>" oppure "AffidyaOssIOS/<ver>";
 *  - sui tablet anche "AffidyaOssTablet/1";
 *  - oggetto window.OssApp.
 */
export const OSS_UA_RE = /AffidyaOss(Android|IOS)\//;
export const OSS_TABLET_UA_RE = /AffidyaOssTablet\//;

export function isNativeOssApp(ua = typeof navigator !== "undefined" ? navigator.userAgent : "") {
  return OSS_UA_RE.test(ua || "");
}

export function isNativeOssTablet(ua = typeof navigator !== "undefined" ? navigator.userAgent : "") {
  return OSS_TABLET_UA_RE.test(ua || "");
}

export function nativeOssPlatform(ua = typeof navigator !== "undefined" ? navigator.userAgent : "") {
  const m = (ua || "").match(OSS_UA_RE);
  return m ? (m[1] === "IOS" ? "ios" : "android") : null;
}

export function getOssBridge() {
  if (typeof window === "undefined") return null;
  const b = window.OssApp;
  return b && typeof b === "object" ? b : null;
}

/** true se il wrapper nativo sa salvare/aprire un file passato come data URL (OssApp.saveFile). */
export function canNativeSaveFile(bridge = getOssBridge()) {
  return Boolean(bridge && typeof bridge.saveFile === "function");
}

export function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

/**
 * Salva un Blob: nell'app OSS nativa passa da OssApp.saveFile (nelle WebView i download blob:
 * e i popup non funzionano), altrimenti download classico. L'URL blob viene revocato in
 * differita: revocarlo subito dopo click() annulla il download su alcuni browser/WebView.
 * @returns {Promise<"native"|"download">}
 */
export async function saveOssBlob(blob, fileName, { bridge = getOssBridge(), doc = document } = {}) {
  const name = fileName || "documento";
  if (canNativeSaveFile(bridge)) {
    bridge.saveFile(name, await blobToDataUrl(blob));
    return "native";
  }
  const url = URL.createObjectURL(blob);
  const a = doc.createElement("a");
  a.href = url;
  a.download = name;
  a.rel = "noopener";
  doc.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
  return "download";
}

/** WebView generica (Android "; wv)" o iOS senza Safari) o app OSS nativa: niente invito all'installazione. */
export function isLikelyWebView(ua = typeof navigator !== "undefined" ? navigator.userAgent : "") {
  if (isNativeOssApp(ua)) return true;
  if (/; wv\)/.test(ua)) return true;
  return /(iPhone|iPad|iPod).*AppleWebKit(?!.*Safari)/.test(ua);
}

export function isStandaloneDisplay() {
  if (typeof window === "undefined") return false;
  const mq = window.matchMedia && window.matchMedia("(display-mode: standalone)").matches;
  return !!mq || window.navigator?.standalone === true;
}
